import {
  addOrderLineItem,
  clearLineItems,
  deleteLineItems,
  orderSelectors,
  updateLineItemSupplier,
  updateOrderBulkOption,
  updateOrderQuantity,
  updateOrderQuantityByCode,
} from 'ducks/orderComponents'
import OrderLineItem from 'pages/ordering/OrderLineItem'
import ProjectOrderPresenter from 'pages/ordering/ProjectOrderPresenter/projectOrderPresenter'
import { HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { useDispatch, useSelector } from 'react-redux'
import { GlobalCartComponentTypes, removeComponentFromExpandedList } from 'reducer/globalCart'
import type { RootState } from 'types/state'
import {
  BulkOptionType,
  ComponentLikeTypeWithCatalogData,
  DistributorDataTypeV2,
} from '../elements/hardwareSelectorV2/types'
import { getComponentTitleFromData } from '../util/hardware'
import { isMountingComponent, mapComponentTypesV2ToV1 } from '../util/misc'
import { useMapOrderLineItemsToHashMap } from './useMapOrderLineItemsToHashMap'

export const useGlobalCart = () => {
  const dispatch = useDispatch()
  // Only show confirmed line items so the global cart is empty until user clicks "Add to Cart"
  const orderableLineItems = useSelector(orderSelectors.getConfirmedLineItems)
  const lineItemsHashMap = useMapOrderLineItemsToHashMap(orderableLineItems)
  // Compute total from state directly so the badge updates when quantity changes on the cart page.
  // getConfirmedLineItems is cached and can return stale refs when the reducer mutates line items in place.
  const totalItemCount = useSelector((state: RootState) => {
    const confirmed = state.orderComponents.lineItems.filter((item) => item.confirmed)
    return ProjectOrderPresenter.getLineItemsTotalQuantity(confirmed)
  })

  const addLineItem = (lineItem: OrderLineItem) => {
    dispatch(addOrderLineItem(lineItem))
  }

  const addLineItemFromV2Component = (
    data: ComponentLikeTypeWithCatalogData,
    orderingDetails: DistributorDataTypeV2,
    quantity?: number,
    bulkOption?: BulkOptionType | null
  ) => {
    // API may return type as .type or .component_type (snake_case). Use both so items
    // appear in the correct cart section (e.g. Panels not Others).
    const componentTypeV2 = data.type ?? (data as { component_type?: typeof data.type }).component_type
    const componentType = mapComponentTypesV2ToV1(componentTypeV2)

    // Create new OrderLineItem instance. confirmed: true so it appears in the global cart
    // (cart uses getConfirmedLineItems).
    const lineItem = new OrderLineItem({
      code: data.code,
      selectedDistributor: orderingDetails.distributor,
      selectedDistributorOrderingData: orderingDetails,
      quantity: quantity ?? 1,
      pricePerUnit: orderingDetails.price_detail.price_ex_vat,
      variantId: orderingDetails.variant_id,
      variantDiscounts: orderingDetails.volume_discounts,
      stockLevels: orderingDetails.stock_levels,
      componentType,
      componentTypeV2,
      status: 'loaded',
      orderingData: data.ordering_v2,
      bulkOption,
      confirmed: true,
      componentTitle: getComponentTitleFromData(data?.data),
    })

    dispatch(addOrderLineItem(lineItem))
  }

  const updateLineItemQty = (uuid: string, quantity: number) => {
    dispatch(updateOrderQuantity({ uuid, quantity }))
  }

  const updateLineItemQtyByCode = (code: string, quantity: number) => {
    dispatch(updateOrderQuantityByCode({ code, quantity }))
  }

  const changeLineItemSupplier = (orderingData: any, supplier: HardwareSupplierFilterKeyType, lineItemUuid: string) => {
    dispatch(updateLineItemSupplier({ data: orderingData, lineItemUuid, supplier }))
  }

  const removeLineItem = (uuid) => {
    dispatch(deleteLineItems({ source: 'uuid', identifiers: [uuid] }))
  }

  const clearCart = () => {
    dispatch(clearLineItems())
  }

  const updateLineItemBulkOption = (uuid: string, bulkOption: BulkOptionType | null) => {
    dispatch(updateOrderBulkOption({ uuid, bulkOption }))
  }

  const tryCollapseAccordion = (uuid: string) => {
    const componentType = orderableLineItems.find((item) => item.uuid === uuid)?.componentTypeV2

    if (!componentType) return

    const validComponentType = isMountingComponent(componentType) ? 'mounting' : componentType // Normalize component type for mounting

    const isLastItem = lineItemsHashMap[validComponentType]?.length === 1 // Check if this is the last item of this type

    if (!isLastItem) return // If not the last item, do not collapse

    dispatch(
      removeComponentFromExpandedList(validComponentType as GlobalCartComponentTypes) // Collapse the accordion if this is the last item of this type
    )
  }

  return {
    orderableLineItems,
    totalItemCount,
    addLineItem,
    addLineItemFromV2Component,
    updateLineItemQty,
    updateLineItemQtyByCode,
    changeLineItemSupplier,
    removeLineItem,
    clearCart,
    tryCollapseAccordion,
    lineItemsHashMap,
    updateLineItemBulkOption,
  }
}
